/**
 * PlaybookLinkageTabs Component
 *
 * Tabbed interface for displaying playbook linkages (Criteria, Confluences, Entry Models).
 * Renders as a controlled component with tab state managed by parent/context.
 * Shows per-tab placeholder when no content is provided.
 *
 * Wrapped in a Card with:
 * - Tabs in CardHeader (fixed position)
 * - Content in CardBody with overflow-y-auto (scroll owned here, not in child lists)
 *
 * Used in both desktop (StrategiesPlaybooksPage) and mobile (MobilePlaybookDetail) layouts.
 */

import React from 'react';

import { Card, CardHeader, CardBody, Tabs, Tab } from '@enfomentis/ui';
import type { LinkageTabKey } from '../context';

// =============================================================================
// Types
// =============================================================================

export interface PlaybookLinkageTabsProps {
  /** Currently active tab */
  activeTab: LinkageTabKey;
  /** Callback when tab selection changes */
  onTabChange: (tab: LinkageTabKey) => void;
  /** Content for Criteria tab (undefined shows placeholder) */
  criteriaContent?: React.ReactNode;
  /** Content for Confluences tab (undefined shows placeholder) */
  confluencesContent?: React.ReactNode;
  /** Content for Entry Models tab (undefined shows placeholder) */
  entryModelsContent?: React.ReactNode;
}

// =============================================================================
// Constants
// =============================================================================

const TAB_CONFIG: Record<LinkageTabKey, { title: string; placeholder: string }> = {
  criteria: {
    title: 'Criteria',
    placeholder: 'No criteria linked yet',
  },
  confluences: {
    title: 'Confluences',
    placeholder: 'No confluences linked yet',
  },
  'entry-models': {
    title: 'Entry Models',
    placeholder: 'No entry models linked yet',
  },
};

const TAB_KEYS: LinkageTabKey[] = ['criteria', 'confluences', 'entry-models'];

// =============================================================================
// Component
// =============================================================================

export const PlaybookLinkageTabs: React.FC<PlaybookLinkageTabsProps> = ({
  activeTab,
  onTabChange,
  criteriaContent,
  confluencesContent,
  entryModelsContent,
}) => {
  // Map tab key to content prop
  const getContent = (tab: LinkageTabKey): React.ReactNode => {
    switch (tab) {
      case 'criteria':
        return criteriaContent;
      case 'confluences':
        return confluencesContent;
      case 'entry-models':
        return entryModelsContent;
      default:
        return undefined;
    }
  };

  const handleSelectionChange = (key: React.Key) => {
    onTabChange(key as LinkageTabKey);
  };

  const currentContent = getContent(activeTab);

  return (
    <Card
      shadow="none"
      radius="sm"
      className="flex-1 min-h-40 flex flex-col border border-default-200"
    >
      <CardHeader className="px-3 py-2 shrink-0">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={handleSelectionChange}
          variant="underlined"
          color="primary"
          aria-label="Playbook linkage tabs"
          classNames={{
            tabList: 'gap-4',
          }}
        >
          {TAB_KEYS.map((tabKey) => {
            const config = TAB_CONFIG[tabKey];
            return <Tab key={tabKey} title={config.title} />;
          })}
        </Tabs>
      </CardHeader>

      <CardBody className="flex-1 overflow-y-auto px-3 py-0">
        {currentContent ?? (
          <p className="text-text-secondary text-sm py-4 text-center">
            {TAB_CONFIG[activeTab]?.placeholder ?? 'No items linked yet'}
          </p>
        )}
      </CardBody>
    </Card>
  );
};
